import { useEffect, useState } from "react";
import { Clock, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ACCESS_MODULES } from "@/lib/access";

const LEVELS = [["none", "No access"], ["read", "Read only"], ["write", "Read & write"]];

/** Company-wide toggle: does an officer's login start their shift, or must they clock in at the kiosk? */
export function OfficerShiftCard() {
  const [on, setOn] = useState(null);
  useEffect(() => { api.get("/officers/access-settings").then((r) => setOn(!!r.data.officer_login_shift)).catch(() => setOn(false)); }, []);
  const toggle = async () => {
    const next = !on;
    setOn(next);
    try { await api.put("/officers/access-settings", { officer_login_shift: next }); toast.success(next ? "Employee login now starts their shift" : "Employees must clock in at the kiosk"); }
    catch (e) { setOn(!next); toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  if (on === null) return null;
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4" data-testid="officer-shift-card">
      <div className="flex items-start gap-3">
        <Clock className="mt-0.5 h-5 w-5 text-primary" />
        <div>
          <div className="text-sm font-semibold text-slate-900">Employee shift starts at login</div>
          <div className="text-xs text-slate-500">{on ? "Signing in to Logerd opens an officer/staff shift and signing out closes it." : "Off — officers and staff must clock in and out at the kiosk (PIN / Face ID) for hours to count."}</div>
        </div>
      </div>
      <button type="button" role="switch" aria-checked={on} onClick={toggle} data-testid="officer-shift-toggle"
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? "bg-primary" : "bg-slate-300"}`}>
        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}

/** Per-officer module access: none / read / write for each console area. */
export function OfficerAccessDialog({ officer, isCare, onClose, onSaved }) {
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPerms({ ...(officer?.permissions || {}) }); }, [officer]);
  const modules = ACCESS_MODULES.filter((m) => !m.careOnly || isCare);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/officers/${officer.id}/permissions`, { permissions: perms });
      toast.success(`Access updated for ${officer.name}`);
      onSaved?.(data);
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={!!officer} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="officer-access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Console access · {officer?.name}</DialogTitle>
          <DialogDescription>Choose what {officer?.name?.split(" ")[0]} can open in the admin console. Kiosk duties are always available.</DialogDescription>
        </DialogHeader>
        {officer && !officer.email && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="access-no-login-hint">
            {officer.name} is PIN-only (no sign-in email). Add an email &amp; password via Edit details so they can open the console.
          </div>
        )}
        <div className="space-y-2">
          {modules.map((m) => (
            <div key={m.key} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2.5" data-testid={`access-row-${m.key}`}>
              <span className="text-sm font-medium text-slate-800">{m.label}</span>
              <div className="flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
                {LEVELS.map(([v, label]) => (
                  <button key={v} type="button" onClick={() => setPerms((p) => ({ ...p, [m.key]: v }))} data-testid={`access-${m.key}-${v}`}
                          className={`h-8 rounded-md px-3 text-xs font-semibold transition ${(perms[m.key] || "none") === v ? (v === "none" ? "bg-slate-700 text-white" : v === "read" ? "bg-amber-500 text-white" : "bg-primary text-white") : "text-slate-600 hover:bg-white"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <button type="button" onClick={onClose} className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
          <button type="button" onClick={save} disabled={saving} data-testid="access-save-btn" className="h-10 rounded-lg bg-primary px-4 text-sm font-semibold text-white disabled:opacity-60 inline-flex items-center gap-2">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save access
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
